/**
 * An email link that failed on its way back from Supabase.
 *
 * When a confirmation link is expired or was already used, Supabase redirects
 * to the site with the failure in the URL —
 * `/#error=access_denied&error_code=otp_expired&error_description=…` — and
 * supabase-js logs it and does nothing else, leaving the fragment in place.
 * The app never read it, so the person landed on the homepage signed out with
 * no explanation.
 *
 * The usual cause is a second email. Every sign-up submit and every resend
 * issues a new link and retires the previous one, so someone who re-submitted
 * the form (the auth logs show one address doing it four times over three
 * days) and then clicked an older email got nothing.
 */

export interface AuthLinkError {
  /**
   * Supabase's error code, as a bare token (anything else reads "unknown").
   * The toast's copy comes only from `describeAuthLinkError`.
   */
  code: string;
}

/**
 * Never show the link's own `error_description`: anyone can make a link to the
 * site with any text in it, and the toast would show that text on our domain.
 * Known codes get fixed copy; everything else gets the generic message.
 */
const KNOWN_ERRORS: Record<string, { title: string; description: string }> = {
  otp_expired: {
    title: "That link has expired or was already used",
    description:
      "Each new email replaces the last, so only the newest link works. Already confirmed? Just sign in. " +
      "Otherwise we can email you a new one.",
  },
  flow_state_expired: {
    title: "That sign-in took too long",
    description: "The sign-in expired before it finished. Try signing in again.",
  },
  flow_state_not_found: {
    title: "That sign-in link didn't work",
    description: "Open the link in the same browser you signed in from, or try signing in again.",
  },
  bad_oauth_state: {
    title: "Google sign-in didn't finish",
    description: "Something interrupted the sign-in. Try signing in again.",
  },
  bad_oauth_callback: {
    title: "Google sign-in didn't finish",
    description: "Something interrupted the sign-in. Try signing in again.",
  },
};

const GENERIC_ERROR = {
  title: "That sign-in link didn't work",
  description: "Something went wrong with the link. Try signing in again.",
};

/** An error code is a short snake_case token; anything else is not one. */
function cleanCode(raw: string | null): string {
  return raw && /^[a-z0-9_]{1,64}$/.test(raw) ? raw : "unknown";
}

/** Keys Supabase adds to a failed redirect. Only these are removed. */
const AUTH_ERROR_KEYS = ["error", "error_code", "error_description", "sb"];

function paramSets(url: URL): URLSearchParams[] {
  // Implicit flow puts it in the fragment, PKCE in the query.
  return [new URLSearchParams(url.hash.replace(/^#/, "")), new URLSearchParams(url.search)];
}

export function readAuthLinkError(href: string): AuthLinkError | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  for (const params of paramSets(url)) {
    // Require Supabase's own keys, so an unrelated `?error=` is never mistaken for one.
    if (!params.has("error_code") && !params.has("error_description")) continue;
    return { code: cleanCode(params.get("error_code") || params.get("error")) };
  }
  return null;
}

/** The same location minus the error, as a router target. */
export function withoutAuthLinkError(href: string): { pathname: string; search: string; hash: string } {
  const url = new URL(href);
  const [hashParams, searchParams] = paramSets(url);
  for (const key of AUTH_ERROR_KEYS) {
    hashParams.delete(key);
    searchParams.delete(key);
  }
  const search = searchParams.toString();
  const hash = hashParams.toString();
  return { pathname: url.pathname, search: search ? `?${search}` : "", hash: hash ? `#${hash}` : "" };
}

/** Codes a fresh link fixes: the link itself was spent or stale. */
export function offersFreshLink(error: AuthLinkError): boolean {
  return error.code === "otp_expired";
}

export function describeAuthLinkError(error: AuthLinkError): { title: string; description: string } {
  return Object.prototype.hasOwnProperty.call(KNOWN_ERRORS, error.code) ? KNOWN_ERRORS[error.code] : GENERIC_ERROR;
}
